/** @prettier */

import {
  Box,
  Grid,
  Button,
  Stack,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  IconButton,
} from '@mui/material';
import {useState} from 'react';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import {useNavigate, useLocation} from 'react-router-dom';
import {isUndefined} from 'lodash';
import {useTranslation} from 'react-i18next';

//interface import
import * as apiIF from '../../Type/api';
//type import
import * as type from '../../Type/TypeDef';
//Style import
import '../../css/common/common.css';

import {iconBlack, iconWhite, limit} from '../../define';
import {numberWithPeriod, roundOff} from '../../functions/convertString';
import NoMoreRegister from '../../component/NoMoreRegister';

/**
 * マイサイト個別サイト情報BOX表示
 * @param props props
 * - isSelected 選択されているかどうか
 * - siteInfoData 1サイトの情報
 * - setViewSubContentsData サイト情報のセット関数
 * - viewSiteData 機器毎の稼働台数
 * - viewSitePwData 機器毎の消費電力
 * @returns JSX
 */
const SiteInfoBox = (props: {
  isSelected: boolean;
  siteInfoData: apiIF.resParameters.siteInformations;
  viewSiteData: type.mysite.siteData | undefined;
  viewSitePwData: type.mysiteProps.siteKWData | undefined;
  setViewSubContentsData: React.Dispatch<
    React.SetStateAction<apiIF.resParameters.siteInformations | undefined>
  >;
}) => {
  const trans = useTranslation().t; //翻訳関数
  const navigate = useNavigate();

  return (
    <Card
      id={`mv-boxSite${props.siteInfoData['site-name']}`}
      raised={true}
      className="siteList__list__card">
      {/** サイトの名前・場所 */}
      <CardHeader
        title={
          <Box
            className="siteList__list__card__header__siteName"
            title={props.siteInfoData['site-name']}>
            {props.siteInfoData['site-name']}
          </Box>
        }
        subheader={
          <Box
            className="siteList__list__card__header__city"
            title={props.siteInfoData['city']}>
            {props.siteInfoData['city']}
          </Box>
        }
        // 未払い状態のゲートウェイがある時に表示する
        action={
          <Box
            className="siteList__list__card__header__unpaid"
            title={trans('MysiteSiteBox.txt.error')}>
            {props.siteInfoData['site-authority'] === 'admin' &&
            (props.siteInfoData['billing-status'] === 'unpaid' ||
              props.siteInfoData['billing-status'] === 'anyUnpaid') ? (
              <IconButton
                href={`/pages/my-site/site-detail/${props.siteInfoData['site-id']}/billing-setting`}>
                <ErrorOutlineRoundedIcon
                  fontSize="large"
                  className="siteList__list__card__header__unpaid__icon"
                />
              </IconButton>
            ) : (
              <></>
            )}
          </Box>
        }
        onClick={() => {
          props.setViewSubContentsData(props.siteInfoData); //サブコンテンツの表示
        }}
        className={`siteList__list__card__header ${
          props.isSelected ? 'siteList__list__card__header--selectedSite' : ''
        }`}
      />
      <CardContent className="siteList__list__card__content">
        <Grid container columnSpacing={2}>
          {/** 消費電力量 */}
          <Grid item xs={12} className="siteList__list__card__content__power">
            {props.viewSitePwData === undefined ||
            props.viewSitePwData.site === undefined
              ? '---'
              : numberWithPeriod(roundOff(props.viewSitePwData.site))}
            <Box
              component="span"
              className="siteList__list__card__content__power__kwh">
              {trans('MysiteSiteBox.txt.unit')}
            </Box>
          </Grid>
          {/** 空調機の運転台数 */}
          <Grid item xs={7}>
            <AllUnitRunNum siteData={props.viewSiteData} />
          </Grid>
          {/** ビル */}
          <Grid item xs={5}>
            <Building
              aboveGround={props.siteInfoData['above-ground']}
              underGround={props.siteInfoData['under-ground']}
            />
          </Grid>
        </Grid>
      </CardContent>
      <CardActions className="siteList__list__card__footer">
        <Grid container alignItems="center">
          <Grid item xs>
            <Button
              id={`mv-linkSiteDetails${props.siteInfoData['site-name']}`}
              onClick={() => {
                props.setViewSubContentsData(props.siteInfoData); //サブコンテンツの表示
                //サイト詳細に移動
                navigate(
                  `/pages/my-site/site-detail/${props.siteInfoData['site-id']}/state`
                );
              }}
              startIcon={
                <img
                  src={iconBlack.SITE_DETAILS.src}
                  alt={`${trans(
                    `MysiteSiteBox.image.alternativeText.${iconBlack.SITE_DETAILS.alt}`
                  )}`}
                  className="siteList__list__card__footer__icon"
                />
              }>
              <Box className="siteList__list__card__footer__text">
                {trans('MysiteSiteBox.buttonTxt.details')}
              </Box>
            </Button>
          </Grid>
          <Grid item xs="auto" className="siteList__list__card__footer__text">
            {trans(
              `MysiteSiteBox.txt.authority.${props.siteInfoData['site-authority']}`
            )}
          </Grid>
        </Grid>
      </CardActions>
    </Card>
  );
};

/**
 * サイト追加
 * @param props
 * - siteNum サイト登録数
 * @returns JSX
 */
const AddSiteBox = (props: {siteNum: number}) => {
  /** ダイアログ開閉フラグ */
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const navigate = useNavigate();
  //URL取得
  const location = useLocation();

  const trans = useTranslation().t; //翻訳関数

  /** サイト追加ボックス押下時処理 */
  const handleAddNewSite = () => {
    //サイト登録数が上限の場合、メッセージを表示する
    if (props.siteNum >= limit.REGISTRATION_NUM_SITE_MAX) {
      setIsOpenDialog(true);
      //サイト登録数が上限に満たない場合、サイト登録画面に遷移する
    } else {
      navigate(`/pages/my-site/add-site${location.search}`);
    }
  };

  return (
    <>
      <Card
        raised={true}
        className="siteList__list__card siteList__list__card--addSite"
        onClick={() => handleAddNewSite()}>
        <CardHeader className="siteList__list__card__header" />
        <CardContent className="siteList__list__card__content">
          <Stack
            alignItems="center"
            justifyContent="center"
            className="siteList__list__card__content__addSite"
            spacing={1}>
            <img
              src={iconBlack.ADD_SITE.src}
              alt={`${trans(
                `MysiteSiteBox.image.alternativeText.${iconBlack.ADD_SITE.alt}`
              )}`}
              className="siteList__list__card__content__addSite__icon"
            />
            <Box>{trans('MysiteSiteBox.buttonTxt.addNewSite')}</Box>
          </Stack>
        </CardContent>
        <CardActions className="siteList__list__card__footer"></CardActions>
      </Card>
      {/* サイト追加ボックス押下時にサイト登録数が上限の場合、メッセージを表示 */}
      <NoMoreRegister
        open={isOpenDialog}
        setOpen={setIsOpenDialog}
        title={trans('MysiteSiteBox.noMoreRegist.title')}
        text={trans('MysiteSiteBox.noMoreRegist.text')}
      />
    </>
  );
};

/**
 * ユニット状態台数表示 個別
 * @param props props
 * - siteStatusNum ユニット台数データ
 * - iconData アイコンデータ
 * @returns JSX
 */
const UnitRunNum = (props: {
  statusNum: type.mysiteProps.unitStatusNum | undefined;
  iconData: string;
}) => {
  let unitNumber: type.mysiteProps.unitStatusNum;

  //状態値がない場合はすべて0表示とする
  if (isUndefined(props.statusNum)) {
    unitNumber = {
      registerNumber: '0',
      operatingNumber: '0',
      malfunctioningNumber: '0',
    };
  } else {
    unitNumber = props.statusNum;
  }

  /** 登録台数フラグ 登録有:true 登録無:false */
  const regUnitFlg = unitNumber['registerNumber'] !== '0';
  /** 異常空調機フラグ エラー有:true エラー無:false */
  const errUnitFlg = unitNumber['malfunctioningNumber'] !== '0';

  const trans = useTranslation().t; //翻訳関数

  return (
    <Grid
      container
      columnSpacing={1}
      alignItems="center"
      className={
        regUnitFlg
          ? ''
          : 'siteList__list__card__content__numList--unregisteredUnit'
      }>
      {/** アイコン */}
      <Grid item xs="auto">
        <Box className="siteList__list__card__content__numList__icon--unit">
          <img
            src={props.iconData}
            alt={`${trans('MysiteSiteBox.image.alternativeText.unitIcon')}`}
            className="siteList__list__card__content__numList__icon--unit__image"
          />
        </Box>
      </Grid>
      {/** 運転台数 */}
      <Grid
        item
        xs="auto"
        className="siteList__list__card__content__numList__unitNum--normal">
        <Box
          component="span"
          className="siteList__list__card__content__numList__unitNum--normal--run">
          {unitNumber['operatingNumber']}
        </Box>
        &nbsp;/&nbsp;{unitNumber['registerNumber']}
      </Grid>
      {/* エラー台数 */}
      <Grid item xs>
        {errUnitFlg ? (
          <Stack alignItems="center">
            <img
              src={iconWhite.ERROR.src}
              alt={`${trans(
                `MysiteSiteBox.image.alternativeText.${iconWhite.ERROR.alt}`
              )}`}
              className="siteList__list__card__content__numList__icon--error"
            />
            <Box className="siteList__list__card__content__numList__unitNum--error">
              {unitNumber['malfunctioningNumber']}
            </Box>
          </Stack>
        ) : (
          <></>
        )}
      </Grid>
    </Grid>
  );
};

/**
 * 空調機の運転台数
 * @param props props
 * - siteData サイトデータ
 * @returns JSX
 */
const AllUnitRunNum = (props: {siteData: type.mysite.siteData | undefined}) => {
  if (isUndefined(props.siteData)) {
    return <></>;
  }

  return (
    <Stack spacing={2} className="siteList__list__card__content__numList">
      <UnitRunNum
        statusNum={props.siteData.aircon}
        iconData={iconBlack.AC_ON.src}
      />
      <UnitRunNum
        statusNum={props.siteData.freezer}
        iconData={iconBlack.FREEZER_ON.src}
      />
      <UnitRunNum
        statusNum={props.siteData.qton}
        iconData={iconBlack.HOT_WATER_ON.src}
      />
    </Stack>
  );
};

/**
 * ビル
 * @param props props
 * - aboveGround 地上階
 * - underGround 地下階
 * @returns JSX
 */
const Building = (props: {
  aboveGround: string | undefined;
  underGround: string | undefined;
}) => {
  const trans = useTranslation().t; //翻訳関数

  //画像URL
  const buildingFloors1 = `${process.env.PUBLIC_URL}/img/mysite/infographic/building/floors-1.svg`;
  const buildingFloors2 = `${process.env.PUBLIC_URL}/img/mysite/infographic/building/floors-2.svg`;
  const buildingFloors3 = `${process.env.PUBLIC_URL}/img/mysite/infographic/building/floors-3.svg`;
  const buildingFloors4 = `${process.env.PUBLIC_URL}/img/mysite/infographic/building/floors-4.svg`;
  const buildingFloors5 = `${process.env.PUBLIC_URL}/img/mysite/infographic/building/floors-5.svg`;
  const buildingFloors6 = `${process.env.PUBLIC_URL}/img/mysite/infographic/building/floors-6.svg`;
  const buildingFloors7 = `${process.env.PUBLIC_URL}/img/mysite/infographic/building/floors-7.svg`;
  const buildingFloors8 = `${process.env.PUBLIC_URL}/img/mysite/infographic/building/floors-8.svg`;
  const buildingFloors9 = `${process.env.PUBLIC_URL}/img/mysite/infographic/building/floors-9.svg`;
  const buildingFloors10 = `${process.env.PUBLIC_URL}/img/mysite/infographic/building/floors-10.svg`;
  const buildingFloors11 = `${process.env.PUBLIC_URL}/img/mysite/infographic/building/floors-11.svg`;
  const buildingFloors12 = `${process.env.PUBLIC_URL}/img/mysite/infographic/building/floors-12.svg`;
  const buildingFloors13 = `${process.env.PUBLIC_URL}/img/mysite/infographic/building/floors-13.svg`;
  const buildingFloors14 = `${process.env.PUBLIC_URL}/img/mysite/infographic/building/floors-14.svg`;
  const buildingFloors15 = `${process.env.PUBLIC_URL}/img/mysite/infographic/building/floors-15.svg`;
  const groundLevelLine = `${process.env.PUBLIC_URL}/img/mysite/infographic/building/ground-level-line.svg`;
  const buildingBasement1 = `${process.env.PUBLIC_URL}/img/mysite/infographic/building/basement-1.svg`;
  const buildingBasement2 = `${process.env.PUBLIC_URL}/img/mysite/infographic/building/basement-2.svg`;
  const buildingBasement3 = `${process.env.PUBLIC_URL}/img/mysite/infographic/building/basement-3.svg`;
  const buildingBasement4 = `${process.env.PUBLIC_URL}/img/mysite/infographic/building/basement-4.svg`;
  //ビル画像一覧
  const buildingFloorsSrc: {[key: string]: string} = {
    '0': buildingFloors1,
    '1': buildingFloors2,
    '2': buildingFloors3,
    '3': buildingFloors4,
    '4': buildingFloors5,
    '5': buildingFloors6,
    '6': buildingFloors7,
    '7': buildingFloors8,
    '8': buildingFloors9,
    '9': buildingFloors10,
    '10': buildingFloors11,
    '11': buildingFloors12,
    '12': buildingFloors13,
    '13': buildingFloors14,
    '14': buildingFloors15,
  };
  const buildingBasementSrc: {[key: string]: string} = {
    '0': '',
    '-1': buildingBasement1,
    '-2': buildingBasement2,
    '-3': buildingBasement3,
    '-4': buildingBasement4,
  };

  //地下フロア名変換テーブル
  const buildingBasementNameTable: {[key: string]: string} = {
    '0': '',
    '-1': trans('MysiteSiteBox.txt.floor.basement1'),
    '-2': trans('MysiteSiteBox.txt.floor.basement2'),
    '-3': trans('MysiteSiteBox.txt.floor.basement3'),
    '-4': trans('MysiteSiteBox.txt.floor.basement4'),
  };

  return (
    <Grid container alignItems="flex-end">
      {/** 地上 */}
      <Grid
        item
        xs="auto"
        className="siteList__list__card__content__building__aboveGround">
        <Stack>
          <img
            src={
              props.aboveGround === undefined
                ? ''
                : buildingFloorsSrc[props.aboveGround]
            }
            alt={`${trans(
              'MysiteSiteBox.image.alternativeText.aboveGroundBuilding'
            )}`}
            className="siteList__list__card__content__building__image"
          />
          {/** 地上地下区切り */}
          <img
            src={groundLevelLine}
            alt={`${trans(
              'MysiteSiteBox.image.alternativeText.groundLevelLine'
            )}`}
            className="siteList__list__card__content__building__image"
          />
        </Stack>
      </Grid>
      <Grid item xs>
        <Box
          className={`siteList__list__card__content__building__aboveGround__text${props.aboveGround}`}>
          {props.aboveGround === undefined
            ? ''
            : trans(`MysiteSiteBox.txt.floor.floor${props.aboveGround}`)}
        </Box>
      </Grid>
      {/** 地下 */}
      {props.underGround !== undefined && props.underGround !== '0' ? (
        <>
          <Grid
            item
            xs="auto"
            className="siteList__list__card__content__building__underGround">
            <img
              src={buildingBasementSrc[props.underGround]}
              alt={`${trans(
                'MysiteSiteBox.image.alternativeText.underGroundBuilding'
              )}`}
              className="siteList__list__card__content__building__image"
            />
          </Grid>
          <Grid
            item
            xs
            className={`siteList__list__card__content__building__underGround__text${props.underGround}`}>
            {buildingBasementNameTable[props.underGround]}
          </Grid>
        </>
      ) : (
        <Grid
          item
          xs={12}
          className="siteList__list__card__content__building__underGround"></Grid>
      )}
    </Grid>
  );
};

export {AddSiteBox};
export default SiteInfoBox;
